import { useState, useEffect, useMemo } from "react";
import { Trophy, Activity, Clock, BarChart3, X, LineChart as LineChartIcon, Table2, Bot, Dog, Bitcoin, Shield, Briefcase, Zap, User as UserIcon } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, Customized } from "recharts";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const CHART_COLORS = [
  "#D4AF37", "#22c55e", "#ef4444", "#3b82f6", "#f59e0b", "#8b5cf6", "#ec4899",
  "#14b8a6", "#f97316", "#6366f1"
];

// Map trader display names → tiny icon + tint
const TRADER_ICONS = {
  "Percival Gains":    { Icon: Bot,       color: "#6B8DE3" }, // robot
  "Rockafeller Jones": { Icon: Dog,       color: "#D4AF37" }, // Monopoly dog
  "CryptoMan":         { Icon: Bitcoin,   color: "#f7931a" }, // Bitcoin orange
  "ConservativeMan":   { Icon: Shield,    color: "#22c55e" }, // defensive green
  "Gordan Gecko":      { Icon: Briefcase, color: "#D4AF37" }, // Wall Street briefcase
  "DayTrader":         { Icon: Zap,       color: "#ef4444" }, // lightning-fast
};

function TraderIcon({ name, className = "" }) {
  const entry = TRADER_ICONS[name];
  const Icon = entry?.Icon || UserIcon;
  const color = entry?.color || "#888";
  return <Icon className={`w-3.5 h-3.5 shrink-0 ${className}`} style={{ color }} strokeWidth={1.75} />;
}

function formatPct(v) {
  if (v === undefined || v === null) return "--";
  return `${v >= 0 ? "+" : ""}${v.toFixed(2)}%`;
}

function formatMoney(v) {
  if (!v) return "$0";
  return `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function getMarketSchedule(isOpen) {
  // Current time in US Eastern
  const nowET = new Date(new Date().toLocaleString("en-US", { timeZone: "America/New_York" }));
  const day = nowET.getDay(); // 0=Sun, 6=Sat

  if (isOpen) {
    // Compute ms until 4:00 PM ET today
    const close = new Date(nowET);
    close.setHours(16, 0, 0, 0);
    return { label: "Closes in", target: close };
  }

  // Market closed — compute next 9:30 AM ET trading day
  const h = nowET.getHours();
  const next = new Date(nowET);
  const isWeekday = day >= 1 && day <= 5;
  const beforeOpenToday = isWeekday && (h < 9 || (h === 9 && nowET.getMinutes() < 30));

  if (!beforeOpenToday) {
    next.setDate(next.getDate() + 1);
    while (next.getDay() === 0 || next.getDay() === 6) next.setDate(next.getDate() + 1);
  }
  next.setHours(9, 30, 0, 0);
  return { label: "Opens in", target: next };
}

function formatCountdown(targetET) {
  const nowET = new Date(new Date().toLocaleString("en-US", { timeZone: "America/New_York" }));
  let ms = targetET.getTime() - nowET.getTime();
  if (ms < 0) ms = 0;
  const totalMin = Math.floor(ms / 60000);
  const d = Math.floor(totalMin / 1440);
  const h = Math.floor((totalMin % 1440) / 60);
  const m = totalMin % 60;
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function Sparkline({ data, isPositive, width = 80, height = 18, baseline = 100000 }) {
  if (!data || data.length < 2) return null;
  // Always include the baseline in the scale so $100k is visually anchored
  const min = Math.min(...data, baseline);
  const max = Math.max(...data, baseline);
  const range = max - min || 1;
  const y = (v) => height - ((v - min) / range) * height;
  const stepX = width / (data.length - 1);
  const points = data.map((v, i) => `${(i * stepX).toFixed(2)},${y(v).toFixed(2)}`).join(" ");
  const color = isPositive ? "#22c55e" : "#ef4444";
  const areaPoints = `0,${height} ${points} ${width},${height}`;
  const gradId = `spark-grad-${Math.random().toString(36).slice(2, 8)}`;
  const baselineY = y(baseline).toFixed(2);
  return (
    <svg width={width} height={height} className="block ml-auto" style={{ overflow: "visible" }}>
      <defs>
        <linearGradient id={gradId} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={areaPoints} fill={`url(#${gradId})`} />
      {/* $100k baseline reference */}
      <line x1="0" x2={width} y1={baselineY} y2={baselineY} stroke="#666" strokeWidth="0.6" strokeDasharray="2 2" opacity="0.6" />
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.2" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

function WindowWinnerBanner({ winner, bench, label, windowKey }) {
  if (!winner) return null;
  const up = winner.period_pct >= 0;
  const alpha = bench && !(winner.is_new && windowKey !== "all") ? +(winner.period_pct - (bench.period_pct || 0)).toFixed(2) : null;
  const Stat = ({ k, v, cls = "text-bt-text-primary", tid }) => (
    <div><p className="text-[10px] font-mono uppercase tracking-wider text-bt-text-tertiary">{k}</p><p className={`font-mono text-sm mt-0.5 ${cls}`} data-testid={tid}>{v}</p></div>
  );
  return (
    <div className="relative overflow-hidden border border-bt-gold/40 bg-bt-gold/5 p-5 flex flex-wrap items-center gap-6" data-testid="window-winner-banner">
      <div className="flex items-center gap-4 min-w-[220px] flex-1">
        <div className="w-12 h-12 bg-bt-gold/20 border border-bt-gold/40 flex items-center justify-center shrink-0"><Trophy className="w-6 h-6 text-bt-gold" /></div>
        <div>
          <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-bt-gold">Top Trader · {label}</p>
          <div className="flex items-center gap-2 mt-1">
            <TraderIcon name={winner.name} className="w-4 h-4" />
            <span className="font-heading text-xl font-semibold text-bt-text-primary" data-testid="window-winner-name">{winner.name}</span>
            {winner.is_new && windowKey !== "all" && <span className="px-1 py-0.5 text-[9px] font-mono border border-bt-gold/40 text-bt-gold whitespace-nowrap" data-testid="window-winner-new-badge">NEW · {winner.days_active}d</span>}
          </div>
        </div>
      </div>
      <Stat k="Return" v={formatPct(winner.period_pct)} cls={`text-2xl font-bold ${up ? "text-bt-positive" : "text-bt-negative"}`} tid="window-winner-return" />
      <Stat k="vs S&P" v={alpha === null ? "--" : `${alpha >= 0 ? "+" : ""}${alpha}%`} cls={alpha === null ? "text-bt-text-tertiary" : alpha >= 0 ? "text-bt-positive" : "text-bt-negative"} tid="window-winner-alpha" />
      <Stat k="Strategy" v={winner.strategy} tid="window-winner-strategy" />
      <Stat k="Trades" v={winner.period_trades} tid="window-winner-trades" />
      <div className="hidden sm:block"><Sparkline data={winner.sparkline} isPositive={up} width={140} height={36} /></div>
    </div>
  );
}

const money = (v) => `$${Math.round(v || 0).toLocaleString()}`;

function MobileLeaderboard({ users, benchmarks, bench, windowLabel, lbWindow, onTrades, onTrader }) {
  let rank = 0;
  return (
    <div className="lg:hidden space-y-2 md:space-y-0 md:grid md:grid-cols-2 md:gap-2" data-testid="leaderboard-mobile-list">
      {users.map((u, i) => {
        const sp = u.is_sp_tracker;
        if (!sp) rank++;
        const isNew = u.is_new && lbWindow !== "all";
        const alpha = bench && !isNew && !sp ? +(u.period_pct - (bench.period_pct || 0)).toFixed(2) : null;
        return (
          <div key={i} className={`bg-bt-surface border p-3 ${sp ? "border-[#6B8DE3]/40" : rank === 1 ? "border-bt-gold/40" : "border-bt-border-subtle"}`} data-testid={`leaderboard-card-${i}`}>
            <div className="flex items-center gap-2">
              <span className={`w-7 text-center font-mono text-sm ${rank === 1 && !sp ? "text-bt-gold font-bold" : "text-bt-text-tertiary"}`}>{sp ? "S&P" : rank}</span>
              {!sp && <TraderIcon name={u.name} />}
              <button onClick={() => onTrader(u.name, sp)} className="font-body text-sm font-medium text-bt-text-primary truncate hover:underline text-left" data-testid={`leaderboard-card-name-${i}`}>{u.name}</button>
              {isNew && <span className="px-1 text-[9px] font-mono border border-bt-gold/40 text-bt-gold whitespace-nowrap">NEW · {u.days_active}d</span>}
              <span className="ml-auto font-mono text-sm text-bt-text-primary">{money(u.portfolio_value)}</span>
            </div>
            <div className="flex items-center gap-3 mt-2 pl-9">
              <div className="font-mono text-[11px] leading-tight">
                <p className="text-bt-text-tertiary uppercase text-[9px]">{windowLabel}</p>
                <p className={u.period_pct >= 0 ? "text-bt-positive" : "text-bt-negative"}>{formatPct(u.period_pct)}</p>
              </div>
              <div className="font-mono text-[11px] leading-tight">
                <p className="text-bt-text-tertiary uppercase text-[9px]">vs S&P</p>
                <p className={alpha === null ? "text-bt-text-tertiary" : alpha >= 0 ? "text-bt-positive" : "text-bt-negative"}>{alpha === null ? "--" : `${alpha >= 0 ? "+" : ""}${alpha}%`}</p>
              </div>
              <div className="font-mono text-[11px] leading-tight">
                <p className="text-bt-text-tertiary uppercase text-[9px]">Today</p>
                <p className={u.today_pct >= 0 ? "text-bt-positive" : "text-bt-negative"}>{formatPct(u.today_pct)}</p>
              </div>
              <div className="ml-auto"><Sparkline data={u.sparkline} isPositive={u.period_pct >= 0} width={70} height={22} /></div>
            </div>
            <div className="flex items-center justify-between mt-2 pl-9 text-[10px] font-mono text-bt-text-tertiary">
              <span className="truncate">{u.strategy}</span>
              {!sp && u.period_trades > 0 ? (
                <button onClick={() => onTrades(u.name, false)} className="text-bt-gold underline underline-offset-2 shrink-0" data-testid={`leaderboard-card-trades-${i}`}>{u.period_trades} trades</button>
              ) : <span className="shrink-0">{u.period_trades || 0} trades</span>}
            </div>
          </div>
        );
      })}
      {benchmarks.map(b => (
        <div key={b.symbol} className="flex items-center gap-2 px-3 py-2 bg-bt-surface/70 border border-bt-border-subtle font-mono text-xs" data-testid={`leaderboard-card-bench-${b.symbol}`}>
          <span className="w-9 text-[10px]" style={{ color: "#6B8DE3" }}>{b.symbol}</span>
          <span className="italic font-body text-sm truncate" style={{ color: "#6B8DE3" }}>{b.name}</span>
          <span className={`ml-auto ${b.period_pct >= 0 ? "text-bt-positive" : "text-bt-negative"}`}>{formatPct(b.period_pct)}</span>
          <span className="w-20 text-right text-bt-text-secondary">{money(b.portfolio_value)}</span>
        </div>
      ))}
    </div>
  );
}

const LB_WINDOWS = [{ key: "1d", label: "1D" }, { key: "1w", label: "1W" }, { key: "1m", label: "1M" }, { key: "3m", label: "3M" }, { key: "all", label: "All" }];

export default function LeaderboardPage() {
  const [data, setData] = useState({ users: [], benchmark: null, stock_market_open: false });
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("period_pct");
  const [lbWindow, setLbWindow] = useState(() => {
    const saved = localStorage.getItem("rj_lb_window");
    return LB_WINDOWS.some(w => w.key === saved) ? saved : "1w";
  });
  const pickWindow = (w) => { setLbWindow(w); localStorage.setItem("rj_lb_window", w); };
  const [selectedTrader, setSelectedTrader] = useState(null);
  const [traderDetail, setTraderDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [view, setView] = useState("table"); // "table" or "chart"
  const [chartData, setChartData] = useState([]);
  const [chartSeries, setChartSeries] = useState([]);
  const [chartBucket, setChartBucket] = useState("auto"); // "auto" | "hour" | "day"
  const [actualBucket, setActualBucket] = useState("hour"); // what backend chose
  const dayMarkers = useMemo(() => {
    const m = [];
    let lastDate = null;
    chartData.forEach(row => {
      const date = (row.time || "").split(" ")[0];
      if (lastDate !== null && date && date !== lastDate) m.push({ x: row.time, label: date });
      if (date) lastDate = date;
    });
    return m;
  }, [chartData]);

  // Build a tick list that always includes the first row, every midnight boundary, and the last row
  const xTicks = useMemo(() => {
    if (!chartData.length) return undefined;
    const set = new Set();
    set.add(chartData[0].time);
    dayMarkers.forEach(m => set.add(m.x));
    set.add(chartData[chartData.length - 1].time);
    // Sample a few intermediate ticks for context
    const stride = Math.max(1, Math.floor(chartData.length / 8));
    for (let i = stride; i < chartData.length - 1; i += stride) {
      set.add(chartData[i].time);
    }
    return Array.from(set);
  }, [chartData, dayMarkers]);
  const [portfolioTrader, setPortfolioTrader] = useState(null); // separate modal for portfolio holdings
  const [tradesTrader, setTradesTrader] = useState(null);
  const [tradesList, setTradesList] = useState([]);
  const [tradesLoading, setTradesLoading] = useState(false);
  const [nowTick, setNowTick] = useState(Date.now()); // re-render every minute for countdown

  useEffect(() => {
    const t = setInterval(() => setNowTick(Date.now()), 30 * 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (view !== "chart") return;
    axios.get(`${BACKEND}/api/woodrow/leaderboard/history`, { params: { bucket: chartBucket } }).then(r => {
      const d = r.data;
      if (!d.labels || !d.series) return;
      const rows = d.labels.map((label, i) => {
        const row = { time: label };
        d.series.forEach(s => { row[s.name] = s.data[i]; });
        return row;
      });
      setChartData(rows);
      setChartSeries(d.series.map(s => ({ name: s.name, isSP: s.is_sp_tracker })));
      setActualBucket(d.bucket || "hour");
    }).catch(() => {});
  }, [view, chartBucket]);

  const openTraderModal = (name, isSP) => {
    if (isSP) return;
    setSelectedTrader(name);
    setDetailLoading(true);
    axios.get(`${BACKEND}/api/woodrow/leaderboard/trader/${encodeURIComponent(name)}`).then(r => {
      setTraderDetail(r.data);
      setDetailLoading(false);
    }).catch(() => setDetailLoading(false));
  };

  const openPortfolioModal = (name, isSP) => {
    if (isSP) return;
    setPortfolioTrader(name);
    // Reuse the same detail payload — it already carries positions/cash/etc.
    setDetailLoading(true);
    axios.get(`${BACKEND}/api/woodrow/leaderboard/trader/${encodeURIComponent(name)}`).then(r => {
      setTraderDetail(r.data);
      setDetailLoading(false);
    }).catch(() => setDetailLoading(false));
  };

  const openTradesModal = (name, isSP) => {
    if (isSP) return;
    setTradesTrader(name);
    setTradesLoading(true);
    setTradesList([]);
    axios.get(`${BACKEND}/api/woodrow/leaderboard/trader/${encodeURIComponent(name)}/trades`, { params: { limit: 200, since: data.window_start || undefined } }).then(r => {
      setTradesList(r.data?.trades || []);
      setTradesLoading(false);
    }).catch(() => setTradesLoading(false));
  };

  useEffect(() => {
    let stale = false;
    const fetch = () => {
      axios.get(`${BACKEND}/api/woodrow/leaderboard`, { params: { window: lbWindow } }).then(r => {
        if (stale) return;
        setData(r.data || { users: [], benchmark: null, stock_market_open: false });
        setLoading(false);
      }).catch(() => setLoading(false));
    };
    fetch();
    const interval = setInterval(fetch, 60 * 1000);
    return () => { stale = true; clearInterval(interval); };
  }, [lbWindow]);

  const sorted = [...(data.users || [])].sort((a, b) => {
    // S&P tracker always goes after regular users
    if (a.is_sp_tracker && !b.is_sp_tracker) return 1;
    if (!a.is_sp_tracker && b.is_sp_tracker) return -1;
    return (b[sortBy] || 0) - (a[sortBy] || 0);
  });
  const bench = data.benchmark;
  const benchmarks = data.benchmarks || [];
  const stockOpen = data.stock_market_open;
  const resetDate = data.reset_date
    ? (() => { const [y, m, d] = data.reset_date.split("-"); return new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "short", day: "numeric" }); })()
    : null;
  const windowLabel = lbWindow === "all" ? (resetDate ? `Since ${resetDate}` : "All Time") : `${lbWindow.toUpperCase()} Return`;

  return (
    <div className="max-w-[1200px] mx-auto px-4 md:px-8 py-8 space-y-8" data-testid="leaderboard-page">
      {/* Header */}
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-bt-text-primary tracking-tight">Leaderboard</h1>
          <p className="text-bt-text-tertiary text-sm font-body mt-1">Woodrow AI trader rankings — names only, no personal details</p>
        </div>
        <div className="flex flex-col items-end gap-1">
          {(() => {
            const sched = getMarketSchedule(stockOpen);
            // eslint-disable-next-line no-unused-vars
            const _tick = nowTick; // re-render every 30s
            return (
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-mono text-bt-text-tertiary" data-testid="market-countdown">
                  {sched.label} {formatCountdown(sched.target)}
                </span>
                <div className="flex items-center gap-1.5">
                  <span className={`relative flex h-2 w-2`}>{stockOpen && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-bt-positive opacity-40" />}<span className={`relative inline-flex rounded-full h-2 w-2 ${stockOpen ? "bg-bt-positive" : "bg-bt-text-tertiary"}`} /></span>
                  <span className={`text-[10px] font-mono ${stockOpen ? "text-bt-positive" : "text-bt-text-tertiary"}`}>STOCKS {stockOpen ? "OPEN" : "CLOSED"}</span>
                </div>
              </div>
            );
          })()}
          <div className="flex items-center gap-1.5">
            <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-bt-positive opacity-40" /><span className="relative inline-flex rounded-full h-2 w-2 bg-bt-positive" /></span>
            <span className="text-[10px] font-mono text-bt-positive">CRYPTO 24/7</span>
          </div>
        </div>
      </div>

      {/* Benchmark Bar */}
      {benchmarks.length > 0 && (
        <div className="bg-bt-surface border border-bt-border-subtle p-4 flex items-center justify-between flex-wrap gap-4" data-testid="leaderboard-benchmark-bar">
          <div className="flex items-center gap-3">
            <BarChart3 className="w-5 h-5 text-bt-gold" strokeWidth={1.5} />
            <span className="text-sm font-mono text-bt-gold uppercase tracking-wider">Benchmarks</span>
          </div>
          <div className="flex items-center gap-6 flex-wrap">
            {benchmarks.map(b => (
              <div key={b.symbol} className="flex items-center gap-2">
                <span className="text-[10px] font-mono text-bt-text-tertiary">{b.symbol}</span>
                <span className={`text-sm font-mono font-bold ${b.today_pct >= 0 ? "text-bt-positive" : "text-bt-negative"}`}>
                  {formatPct(b.today_pct)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* View Toggle + Sort Controls */}
      <div className="flex items-center justify-between gap-2">
        {view === "table" && (
          <div className="flex lg:hidden gap-2 flex-1 min-w-0 md:max-w-md" data-testid="leaderboard-mobile-controls">
            <select value={lbWindow} onChange={(e) => pickWindow(e.target.value)} data-testid="mobile-window-select"
              className="flex-1 min-w-0 h-9 px-2 bg-bt-surface border border-bt-border-subtle text-bt-text-primary font-mono text-xs uppercase">
              {LB_WINDOWS.map(w => <option key={w.key} value={w.key}>{w.label === "All" ? "All time" : w.label}</option>)}
            </select>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} data-testid="mobile-sort-select"
              className="flex-1 min-w-0 h-9 px-2 bg-bt-surface border border-bt-border-subtle text-bt-text-primary font-mono text-xs uppercase">
              <option value="period_pct">Sort: Return</option>
              <option value="today_pct">Sort: Today</option>
              <option value="period_trades">Sort: Most active</option>
              <option value="portfolio_value">Sort: Value</option>
            </select>
          </div>
        )}
        <div className={`${view === "table" ? "hidden lg:flex" : "flex"} flex-wrap gap-2`}>
          {view === "table" && (
            <div className="flex mr-2 border border-bt-border-subtle" data-testid="leaderboard-window-filter">
              {LB_WINDOWS.map(w => (
                <button key={w.key} onClick={() => pickWindow(w.key)} data-testid={`window-${w.key}`}
                  className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider transition-colors ${lbWindow === w.key ? "bg-bt-gold/20 text-bt-gold" : "bg-bt-surface text-bt-text-tertiary hover:text-bt-text-secondary"}`}>
                  {w.label}
                </button>
              ))}
            </div>
          )}
          {view === "table" && [
            { key: "period_pct", label: windowLabel },
            { key: "today_pct", label: "Today" },
            { key: "period_trades", label: "Most Active" },
            { key: "portfolio_value", label: "Portfolio Value" },
          ].map(s => (
            <button
              key={s.key}
              onClick={() => setSortBy(s.key)}
              className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider border transition-all ${
                sortBy === s.key
                  ? "bg-bt-gold/15 border-bt-gold/40 text-bt-gold"
                  : "bg-bt-surface border-bt-border-subtle text-bt-text-tertiary hover:text-bt-text-secondary hover:border-bt-border-focus"
              }`}
              data-testid={`sort-${s.key}`}
            >
              {s.label}
            </button>
          ))}
          {view === "chart" && [
            { key: "auto", label: "Auto" },
            { key: "hour", label: "Hourly" },
            { key: "day", label: "Daily" },
          ].map(b => (
            <button
              key={b.key}
              onClick={() => setChartBucket(b.key)}
              className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider border transition-all ${
                chartBucket === b.key
                  ? "bg-bt-gold/15 border-bt-gold/40 text-bt-gold"
                  : "bg-bt-surface border-bt-border-subtle text-bt-text-tertiary hover:text-bt-text-secondary hover:border-bt-border-focus"
              }`}
              data-testid={`chart-bucket-${b.key}`}
              title={b.key === "auto" ? `Currently: ${actualBucket}` : ""}
            >
              {b.label}{b.key === "auto" && chartBucket === "auto" ? ` · ${actualBucket}` : ""}
            </button>
          ))}
        </div>
        <div className="flex gap-1">
          <button
            onClick={() => setView("table")}
            className={`p-2 border transition-all ${view === "table" ? "bg-bt-gold/15 border-bt-gold/40 text-bt-gold" : "bg-bt-surface border-bt-border-subtle text-bt-text-tertiary hover:text-bt-text-secondary"}`}
            data-testid="view-table"
          >
            <Table2 className="w-4 h-4" />
          </button>
          <button
            onClick={() => setView("chart")}
            className={`p-2 border transition-all ${view === "chart" ? "bg-bt-gold/15 border-bt-gold/40 text-bt-gold" : "bg-bt-surface border-bt-border-subtle text-bt-text-tertiary hover:text-bt-text-secondary"}`}
            data-testid="view-chart"
          >
            <LineChartIcon className="w-4 h-4" />
          </button>
        </div>
      </div>

      {view === "table" && !loading && (
        <WindowWinnerBanner winner={(data.users || []).find(u => !u.is_sp_tracker)} bench={bench} label={windowLabel} windowKey={lbWindow} />
      )}

      {/* Chart View */}
      {loading && (
        <div className="text-center py-20 text-bt-text-tertiary font-mono">Loading...</div>
      )}

      {!loading && sorted.length === 0 && view === "table" && (
        <div className="text-center py-20 text-bt-text-tertiary font-mono">No traders yet</div>
      )}

      {view === "chart" && (
        <div className="border border-bt-border-subtle bg-bt-surface p-6" data-testid="leaderboard-chart">
          {chartData.length === 0 ? (
            <div className="text-center py-20 text-bt-text-tertiary font-mono">Loading chart data...</div>
          ) : (
            <ResponsiveContainer width="100%" height={480}>
              <LineChart data={chartData} margin={{ top: 60, right: 30, left: 20, bottom: 30 }}>
                <XAxis
                  dataKey="time"
                  tick={(props) => {
                    const { x, y, payload } = props;
                    const v = payload?.value || "";
                    const parts = v.split(" ");
                    const isMidnight = parts.length === 2 && parts[1] === "12AM";
                    const isFirstOrDateOnly = parts.length === 1 || props.index === 0;
                    if (isMidnight) {
                      return (
                        <g transform={`translate(${x},${y})`}>
                          <line x1={0} x2={0} y1={-380} y2={-4} stroke="rgb(var(--bt-gold))" strokeOpacity={0.45} strokeWidth={1} strokeDasharray="3 4" />
                          <text x={4} y={-372} textAnchor="start" fill="rgb(var(--bt-gold))" fontSize={11} fontFamily="'IBM Plex Mono', monospace" fontWeight={700}>{parts[0]}</text>
                        </g>
                      );
                    }
                    return (
                      <g transform={`translate(${x},${y})`}>
                        <text x={0} y={14} textAnchor="middle" fill="rgb(var(--bt-text-secondary))" fontSize={12} fontFamily="'IBM Plex Mono', monospace">
                          {isFirstOrDateOnly ? v : parts[1]}
                        </text>
                      </g>
                    );
                  }}
                  tickLine={{ stroke: "rgb(var(--bt-border-focus))" }}
                  axisLine={{ stroke: "rgb(var(--bt-border-focus))" }}
                  ticks={xTicks}
                  interval={0}
                  height={40}
                />
                <YAxis
                  tick={{ fill: "rgb(var(--bt-text-secondary))", fontSize: 12, fontFamily: "'IBM Plex Mono', monospace" }}
                  tickLine={{ stroke: "rgb(var(--bt-border-focus))" }}
                  axisLine={{ stroke: "rgb(var(--bt-border-focus))" }}
                  tickFormatter={v => `$${(v/1000).toFixed(0)}k`}
                  domain={["dataMin - 500", "dataMax + 500"]}
                />
                <Tooltip
                  contentStyle={{ background: "rgb(var(--bt-surface))", border: "1px solid rgb(var(--bt-border-focus))", fontFamily: "'IBM Plex Mono', monospace", fontSize: 12 }}
                  labelStyle={{ color: "rgb(var(--bt-text-primary))", fontWeight: 600 }}
                  itemStyle={{ color: "rgb(var(--bt-text-primary))" }}
                  formatter={(value, name) => [`$${Number(value).toLocaleString(undefined, {maximumFractionDigits: 0})}`, name]}
                />
                <Legend
                  verticalAlign="top"
                  align="center"
                  wrapperStyle={{ fontSize: 13, fontFamily: "'IBM Plex Sans', sans-serif", color: "rgb(var(--bt-text-primary))", paddingBottom: 16 }}
                  iconType="circle"
                  iconSize={11}
                  formatter={(value) => <span style={{ color: "rgb(var(--bt-text-primary))", marginRight: 18, fontWeight: 500 }}>{value}</span>}
                />
                {chartSeries.map((s, i) => (
                  <Line
                    key={s.name}
                    type="monotone"
                    dataKey={s.name}
                    stroke={s.isSP ? "#6B8DE3" : CHART_COLORS[i % CHART_COLORS.length]}
                    strokeWidth={s.isSP ? 2.5 : 2}
                    strokeDasharray={s.isSP ? "6 3" : undefined}
                    dot={false}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      )}

      {/* Table View */}
      {view === "table" && !loading && sorted.length > 0 && (
        <>
        <MobileLeaderboard users={sorted} benchmarks={benchmarks} bench={bench} windowLabel={windowLabel} lbWindow={lbWindow} onTrades={openTradesModal} onTrader={openTraderModal} />
        <div className="hidden lg:block border border-bt-border-subtle bg-bt-surface" data-testid="leaderboard-table">
          {/* Table Header */}
          <div className="lb-grid grid grid-cols-[40px_minmax(140px,1fr)_90px_80px_50px_75px_55px_85px_55px_60px_50px_145px] gap-2 px-5 py-3 bg-bt-surface-2 border-b border-bt-border-subtle text-[10px] font-mono uppercase tracking-wider text-bt-text-tertiary">
            <div className="text-center">#</div>
            <div>Trader</div>
            <div className="text-right">Portfolio</div>
            <div className="text-right">Cash</div>
            <div className="text-right">Pos</div>
            <div className="text-right">Today</div>
            <div className="text-right" style={{color: "#6B8DE3"}}>vs S&P</div>
            <div className="text-right" data-testid="leaderboard-window-header">{windowLabel}</div>
            <div className="text-right" style={{color: "#6B8DE3"}}>vs S&P</div>
            <div className="text-right hidden sm:block">Trades</div>
            <div className="text-right hidden sm:block">Days</div>
            <div className="text-right hidden md:block">Strategy</div>
          </div>

          {/* Rows */}
          {(() => {
            let userRank = 0;
            return sorted.map((user, i) => {
            const isSPTracker = user.is_sp_tracker;
            if (!isSPTracker) userRank++;
            const isTop = userRank === 1 && !isSPTracker;
            const isPositive = user.period_pct >= 0;
            return (
              <div
                key={i}
                className={`lb-grid grid grid-cols-[40px_minmax(140px,1fr)_90px_80px_50px_75px_55px_85px_55px_60px_50px_145px] gap-2 px-5 py-4 border-b border-bt-border-subtle items-center transition-colors hover:bg-bt-surface-2/50 ${
                  isSPTracker ? "bg-[#6B8DE3]/5 border-l-2 border-l-[#6B8DE3]/40" : isTop ? "bg-bt-gold/5" : ""
                }`}
                data-testid={`leaderboard-row-${i}`}
              >
                {/* Rank */}
                <div className="text-center">
                  {isSPTracker ? (
                    <span className="inline-flex items-center justify-center w-7 h-7 text-[10px] font-mono" style={{color: "#6B8DE3"}}>S&P</span>
                  ) : isTop ? (
                    <span className="inline-flex items-center justify-center w-7 h-7 bg-bt-gold/20 border border-bt-gold/40 text-bt-gold font-mono text-sm font-bold">1</span>
                  ) : (
                    <span className="text-bt-text-tertiary font-mono text-sm">{userRank}</span>
                  )}
                </div>

                {/* Name */}
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-1.5">
                    {!isSPTracker && <TraderIcon name={user.name} />}
                    <p
                      className={`font-body text-sm font-medium cursor-pointer hover:underline whitespace-nowrap ${isSPTracker ? "" : isTop ? "text-bt-gold" : "text-bt-text-primary"}`}
                      style={isSPTracker ? {color: "#6B8DE3"} : {}}
                      onClick={() => openTraderModal(user.name, isSPTracker)}
                    >
                      {user.name}
                    </p>
                    {user.is_new && lbWindow !== "all" && (
                      <span className="px-1 py-0.5 text-[9px] font-mono border border-bt-gold/40 text-bt-gold whitespace-nowrap" title="Joined during this window, measured from their $100k start" data-testid={`leaderboard-new-badge-${i}`}>
                        NEW · {user.days_active}d
                      </span>
                    )}
                  </div>
                </div>

                {/* Portfolio Value + Sparkline */}
                <div className="text-right">
                  {isSPTracker ? (
                    <span className={`font-mono text-sm font-semibold ${isPositive ? "text-bt-text-primary" : "text-bt-negative"}`}>
                      {formatMoney(user.portfolio_value)}
                    </span>
                  ) : (
                    <button
                      onClick={() => openPortfolioModal(user.name, isSPTracker)}
                      className={`font-mono text-sm font-semibold cursor-pointer hover:underline ${isPositive ? "text-bt-text-primary" : "text-bt-negative"}`}
                      data-testid={`leaderboard-portfolio-${i}`}
                    >
                      {formatMoney(user.portfolio_value)}
                    </button>
                  )}
                  {user.sparkline && user.sparkline.length >= 2 && (
                    <div className="mt-0.5" data-testid={`leaderboard-sparkline-${i}`}>
                      <Sparkline data={user.sparkline} isPositive={isPositive} />
                    </div>
                  )}
                </div>

                {/* Cash */}
                <div className="text-right">
                  <span className="font-mono text-sm text-bt-text-secondary">
                    {formatMoney(user.cash)}
                  </span>
                </div>

                {/* Positions */}
                <div className="text-right">
                  <div className="flex items-center justify-end gap-1.5">
                    {user.long_count > 0 && (
                      <span className="text-xs font-mono text-bt-positive">{user.long_count}L</span>
                    )}
                    {user.short_count > 0 && (
                      <span className="text-xs font-mono text-bt-negative">{user.short_count}S</span>
                    )}
                    {user.positions === 0 && (
                      <span className="text-xs font-mono text-bt-text-tertiary">--</span>
                    )}
                  </div>
                </div>

                {/* Today */}
                <div className="text-right">
                  <span className={`font-mono text-sm font-medium ${user.today_pct >= 0 ? "text-bt-positive" : "text-bt-negative"}`}>
                    {formatPct(user.today_pct)}
                  </span>
                </div>

                {/* vs S&P Today */}
                <div className="text-right">
                  {bench && (() => {
                    const alpha = +(user.today_pct - bench.today_pct).toFixed(2);
                    return <span className={`font-mono text-xs font-medium ${alpha >= 0 ? "text-bt-positive" : "text-bt-negative"}`}>{alpha >= 0 ? "+" : ""}{alpha}%</span>;
                  })()}
                </div>

                {/* All Time */}
                <div className="text-right">
                  <span className={`font-mono text-sm font-bold ${isPositive ? "text-bt-positive" : "text-bt-negative"}`}>
                    {formatPct(user.period_pct)}
                  </span>
                </div>

                {/* vs S&P All Time */}
                <div className="text-right">
                  {user.is_new && lbWindow !== "all" ? <span className="font-mono text-xs text-bt-text-tertiary" title="Joined during this window">--</span> : bench && (() => {
                    const alpha = +(user.period_pct - (bench.period_pct || 0)).toFixed(2);
                    return <span className={`font-mono text-xs font-medium ${alpha >= 0 ? "text-bt-positive" : "text-bt-negative"}`}>{alpha >= 0 ? "+" : ""}{alpha}%</span>;
                  })()}
                </div>

                {/* Trades */}
                <div className="text-right hidden sm:flex items-center justify-end gap-1">
                  <Activity className="w-3 h-3 text-bt-text-tertiary" />
                  {user.period_trades > 0 && !isSPTracker ? (
                    <button
                      onClick={() => openTradesModal(user.name, isSPTracker)}
                      className="font-mono text-xs text-bt-text-secondary cursor-pointer hover:underline hover:text-bt-gold"
                      data-testid={`leaderboard-trades-${i}`}
                    >
                      {user.period_trades}
                    </button>
                  ) : (
                    <span className="font-mono text-xs text-bt-text-secondary">{user.period_trades}</span>
                  )}
                </div>

                {/* Days Active */}
                <div className="text-right hidden sm:flex items-center justify-end gap-1">
                  <Clock className="w-3 h-3 text-bt-text-tertiary" />
                  <span className="font-mono text-xs text-bt-text-secondary">{user.days_active}d</span>
                </div>

                {/* Strategy */}
                <div className="text-right hidden md:block">
                  <span className="text-[11px] font-mono text-bt-text-tertiary whitespace-nowrap">{user.strategy}</span>
                </div>
              </div>
            );
          });
          })()}

          {/* Benchmark Rows */}
          {benchmarks.map(b => (
            <div key={b.symbol} className="lb-grid grid grid-cols-[40px_minmax(140px,1fr)_90px_80px_50px_75px_55px_85px_55px_60px_50px_145px] gap-2 px-5 py-3 bg-bt-surface-2/30 items-center border-b border-bt-border-subtle last:border-b-0" data-testid={`leaderboard-bench-${b.symbol}`}>
              <div className="text-center"><span className="font-mono text-[10px]" style={{color: "#6B8DE3"}}>{b.symbol}</span></div>
              <div><span className="text-sm font-body italic" style={{color: "#6B8DE3"}}>{b.name}</span></div>
              <div className="text-right">
                <span className={`font-mono text-sm font-semibold ${b.all_time_pct >= 0 ? "text-bt-text-primary" : "text-bt-negative"}`}>{formatMoney(b.portfolio_value)}</span>
              </div>
              <div className="text-right"><span className="font-mono text-sm text-bt-text-secondary">$0</span></div>
              <div className="text-right">
                {b.long_count > 0 ? <span className="text-xs font-mono text-bt-positive">{b.long_count}L</span> : <span className="text-xs font-mono text-bt-text-tertiary">--</span>}
              </div>
              <div className="text-right">
                <span className={`font-mono text-sm ${b.today_pct >= 0 ? "text-bt-positive" : "text-bt-negative"}`}>{formatPct(b.today_pct)}</span>
              </div>
              <div className="text-right"><span className="font-mono text-xs text-bt-text-tertiary">--</span></div>
              <div className="text-right">
                <span className={`font-mono text-sm ${b.period_pct >= 0 ? "text-bt-positive" : "text-bt-negative"}`}>{formatPct(b.period_pct)}</span>
              </div>
              <div className="text-right"><span className="font-mono text-xs text-bt-text-tertiary">--</span></div>
              <div className="text-right hidden sm:flex items-center justify-end gap-1">
                <Activity className="w-3 h-3 text-bt-text-tertiary" />
                <span className="font-mono text-xs text-bt-text-secondary">{b.total_trades || 1}</span>
              </div>
              <div className="text-right hidden sm:flex items-center justify-end gap-1">
                <Clock className="w-3 h-3 text-bt-text-tertiary" />
                <span className="font-mono text-xs text-bt-text-secondary">{b.days_active || 0}d</span>
              </div>
              <div className="text-right hidden md:block">
                <span className="text-[11px] font-mono" style={{color: "#6B8DE3"}}>Index</span>
              </div>
            </div>
          ))}
        </div>
        </>
      )}

      {/* Trader Strategy Modal */}
      {selectedTrader && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setSelectedTrader(null)} data-testid="trader-modal-overlay">
          <div className="bg-bt-bg border border-bt-border-subtle w-full max-w-lg mx-4 shadow-2xl" onClick={e => e.stopPropagation()} data-testid="trader-modal">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-bt-border-subtle">
              <h2 className="font-heading text-lg font-semibold text-bt-gold">{selectedTrader}</h2>
              <button onClick={() => setSelectedTrader(null)} className="text-bt-text-tertiary hover:text-bt-text-primary transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            {detailLoading ? (
              <div className="p-8 text-center text-bt-text-tertiary font-mono">Loading...</div>
            ) : traderDetail && !traderDetail.error ? (
              <div className="p-6 space-y-5">
                {/* Strategy Settings */}
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { label: "Risk", value: traderDetail.strategy.risk_tolerance },
                    { label: "Volatility", value: traderDetail.strategy.volatility_preference },
                    { label: "Hold Time", value: traderDetail.strategy.hold_time?.replace("_", " ") },
                  ].map(s => (
                    <div key={s.label} className="bg-bt-surface border border-bt-border-subtle p-3 text-center">
                      <p className="text-[9px] font-mono uppercase tracking-wider text-bt-text-tertiary">{s.label}</p>
                      <p className="text-sm font-mono font-medium text-bt-text-primary mt-1 capitalize">{s.value}</p>
                    </div>
                  ))}
                </div>

                {/* Risk Parameters */}
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: "Max Position", value: `${traderDetail.strategy.max_position_pct}%` },
                    { label: "Cash Reserve", value: `${traderDetail.strategy.cash_reserve_pct}%` },
                    { label: "Stop Loss", value: `${traderDetail.strategy.stop_loss_pct}%` },
                    { label: "Trailing Stop", value: `${traderDetail.strategy.trailing_stop_pct}%` },
                    { label: "Max Drawdown", value: `${traderDetail.strategy.max_drawdown_pct}%` },
                    { label: "Trade Freq", value: `${traderDetail.strategy.trade_frequency_min} min` },
                  ].map(s => (
                    <div key={s.label} className="flex justify-between items-center py-1.5 px-2 border-b border-bt-border-subtle">
                      <span className="text-xs font-mono text-bt-text-tertiary">{s.label}</span>
                      <span className="text-xs font-mono text-bt-text-primary font-medium">{s.value}</span>
                    </div>
                  ))}
                </div>

                {/* Sector Weights */}
                {Object.keys(traderDetail.sector_weights || {}).length > 0 && (
                  <div>
                    <p className="text-[9px] font-mono uppercase tracking-wider text-bt-gold mb-2">Sector Allocation</p>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(traderDetail.sector_weights).filter(([,v]) => v > 0).map(([k, v]) => (
                        <span key={k} className="text-[11px] font-mono bg-bt-surface border border-bt-border-subtle px-2 py-1 text-bt-text-secondary">
                          {k}: {v}%
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Current Positions */}
                {traderDetail.positions?.length > 0 && (
                  <div>
                    <p className="text-[9px] font-mono uppercase tracking-wider text-bt-gold mb-2">Open Positions</p>
                    <div className="flex flex-wrap gap-2">
                      {traderDetail.positions.map(p => (
                        <span key={p.symbol} className={`text-[11px] font-mono px-2 py-1 border ${p.type === "short" ? "border-bt-negative/30 text-bt-negative" : "border-bt-positive/30 text-bt-positive"}`}>
                          {p.symbol} {p.pct_of_portfolio}%
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Portfolio Summary */}
                <div className="flex items-center justify-between pt-3 border-t border-bt-border-subtle">
                  <div>
                    <p className="text-[9px] font-mono uppercase text-bt-text-tertiary">Portfolio</p>
                    <p className="text-sm font-mono font-bold text-bt-text-primary">{formatMoney(traderDetail.portfolio_value)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[9px] font-mono uppercase text-bt-text-tertiary">Cash</p>
                    <p className="text-sm font-mono text-bt-text-secondary">{traderDetail.cash_pct}%</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[9px] font-mono uppercase text-bt-text-tertiary">Total Trades</p>
                    <p className="text-sm font-mono text-bt-text-secondary">{traderDetail.total_trades}</p>
                  </div>
                </div>

                {/* Last Assessment */}
                {traderDetail.last_assessment && (
                  <div className="pt-3 border-t border-bt-border-subtle">
                    <p className="text-[9px] font-mono uppercase tracking-wider text-bt-gold mb-1">AI Assessment</p>
                    <p className="text-xs font-body text-bt-text-secondary leading-relaxed">{traderDetail.last_assessment}</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="p-8 text-center text-bt-text-tertiary font-mono">Trader not found</div>
            )}
          </div>
        </div>
      )}

      {/* Portfolio Holdings Modal */}
      {portfolioTrader && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setPortfolioTrader(null)} data-testid="portfolio-modal-overlay">
          <div className="bg-bt-bg border border-bt-border-subtle w-full max-w-2xl mx-4 shadow-2xl max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="portfolio-modal">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-bt-border-subtle sticky top-0 bg-bt-bg z-10">
              <div>
                <h2 className="font-heading text-lg font-semibold text-bt-gold">{portfolioTrader}</h2>
                <p className="text-[10px] font-mono uppercase tracking-wider text-bt-text-tertiary mt-0.5">Portfolio Holdings</p>
              </div>
              <button onClick={() => setPortfolioTrader(null)} className="text-bt-text-tertiary hover:text-bt-text-primary transition-colors" data-testid="portfolio-modal-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            {detailLoading ? (
              <div className="p-8 text-center text-bt-text-tertiary font-mono">Loading...</div>
            ) : traderDetail && !traderDetail.error ? (
              <div className="p-6 space-y-5">
                {/* Portfolio Summary Stats */}
                <div className="grid grid-cols-4 gap-3">
                  <div className="bg-bt-surface border border-bt-border-subtle p-3">
                    <p className="text-[9px] font-mono uppercase tracking-wider text-bt-text-tertiary">Total Value</p>
                    <p className="text-sm font-mono font-bold text-bt-text-primary mt-1">{formatMoney(traderDetail.portfolio_value)}</p>
                  </div>
                  <div className="bg-bt-surface border border-bt-border-subtle p-3">
                    <p className="text-[9px] font-mono uppercase tracking-wider text-bt-text-tertiary">Cash</p>
                    <p className="text-sm font-mono font-bold text-bt-text-secondary mt-1">{formatMoney(traderDetail.cash)}</p>
                  </div>
                  <div className="bg-bt-surface border border-bt-border-subtle p-3">
                    <p className="text-[9px] font-mono uppercase tracking-wider text-bt-text-tertiary">Invested</p>
                    <p className="text-sm font-mono font-bold text-bt-text-secondary mt-1">{formatMoney(traderDetail.market_value)}</p>
                  </div>
                  <div className="bg-bt-surface border border-bt-border-subtle p-3">
                    <p className="text-[9px] font-mono uppercase tracking-wider text-bt-text-tertiary">Cash %</p>
                    <p className="text-sm font-mono font-bold text-bt-text-secondary mt-1">{traderDetail.cash_pct}%</p>
                  </div>
                </div>

                {/* Positions Table */}
                {traderDetail.positions?.length > 0 ? (
                  <div>
                    <p className="text-[9px] font-mono uppercase tracking-wider text-bt-gold mb-2">Open Positions ({traderDetail.positions.length})</p>
                    <div className="border border-bt-border-subtle overflow-x-auto">
                      {/* Positions header */}
                      <div className="grid min-w-[520px] grid-cols-[70px_50px_70px_1fr_1fr_1fr_60px] gap-2 px-3 py-2 bg-bt-surface-2 border-b border-bt-border-subtle text-[9px] font-mono uppercase tracking-wider text-bt-text-tertiary">
                        <div>Symbol</div>
                        <div className="text-center">Side</div>
                        <div className="text-right">Shares</div>
                        <div className="text-right">Avg</div>
                        <div className="text-right">Last</div>
                        <div className="text-right">P&L</div>
                        <div className="text-right">%Port</div>
                      </div>
                      {traderDetail.positions.map(p => (
                        <div key={p.symbol} className="grid min-w-[520px] grid-cols-[70px_50px_70px_1fr_1fr_1fr_60px] gap-2 px-3 py-2.5 border-b border-bt-border-subtle last:border-b-0 items-center text-xs font-mono hover:bg-bt-surface-2/50 transition-colors" data-testid={`portfolio-pos-${p.symbol}`}>
                          <div className="text-bt-text-primary font-semibold">{p.symbol}</div>
                          <div className="text-center">
                            <span className={`text-[10px] px-1.5 py-0.5 border ${p.type === "short" ? "border-bt-negative/40 text-bt-negative" : "border-bt-positive/40 text-bt-positive"}`}>
                              {p.type === "short" ? "SHORT" : "LONG"}
                            </span>
                          </div>
                          <div className="text-right text-bt-text-secondary">{p.shares}</div>
                          <div className="text-right text-bt-text-secondary">${p.avg_price?.toFixed(2)}</div>
                          <div className="text-right text-bt-text-primary">${p.current_price?.toFixed(2)}</div>
                          <div className="text-right">
                            <div className={`${p.pnl >= 0 ? "text-bt-positive" : "text-bt-negative"}`}>
                              {p.pnl >= 0 ? "+" : ""}{formatMoney(p.pnl)}
                            </div>
                            <div className={`text-[10px] ${p.pnl_pct >= 0 ? "text-bt-positive/70" : "text-bt-negative/70"}`}>
                              {p.pnl_pct >= 0 ? "+" : ""}{p.pnl_pct}%
                            </div>
                          </div>
                          <div className="text-right text-bt-text-tertiary">{p.pct_of_portfolio}%</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-bt-text-tertiary text-sm font-mono">No open positions — 100% cash</div>
                )}

                {/* Last Assessment */}
                {traderDetail.last_assessment && (
                  <div className="pt-3 border-t border-bt-border-subtle">
                    <p className="text-[9px] font-mono uppercase tracking-wider text-bt-gold mb-1">AI Assessment</p>
                    <p className="text-xs font-body text-bt-text-secondary leading-relaxed">{traderDetail.last_assessment}</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="p-8 text-center text-bt-text-tertiary font-mono">Portfolio not available</div>
            )}
          </div>
        </div>
      )}

      {/* Trade History Modal */}
      {tradesTrader && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setTradesTrader(null)} data-testid="trades-modal-overlay">
          <div className="bg-bt-bg border border-bt-border-subtle w-full max-w-3xl mx-4 shadow-2xl max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="trades-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b border-bt-border-subtle sticky top-0 bg-bt-bg z-10">
              <div>
                <h2 className="font-heading text-lg font-semibold text-bt-gold">{tradesTrader}</h2>
                <p className="text-[10px] font-mono uppercase tracking-wider text-bt-text-tertiary mt-0.5">Trade History ({tradesList.length})</p>
              </div>
              <button onClick={() => setTradesTrader(null)} className="text-bt-text-tertiary hover:text-bt-text-primary transition-colors" data-testid="trades-modal-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            {tradesLoading ? (
              <div className="p-8 text-center text-bt-text-tertiary font-mono">Loading...</div>
            ) : tradesList.length === 0 ? (
              <div className="p-8 text-center text-bt-text-tertiary font-mono">No trades yet</div>
            ) : (
              <div className="p-6">
                <div className="border border-bt-border-subtle overflow-x-auto">
                  {/* Header */}
                  <div className="grid min-w-[620px] grid-cols-[85px_60px_60px_70px_80px_80px_1fr] gap-2 px-3 py-2 bg-bt-surface-2 border-b border-bt-border-subtle text-[9px] font-mono uppercase tracking-wider text-bt-text-tertiary">
                    <div>When</div>
                    <div>Action</div>
                    <div>Symbol</div>
                    <div className="text-right">Shares</div>
                    <div className="text-right">Price</div>
                    <div className="text-right">P&L</div>
                    <div>Reason</div>
                  </div>
                  {tradesList.map((t, idx) => {
                    const dt = t.timestamp ? new Date(t.timestamp) : null;
                    const when = dt ? dt.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
                    const isBuy = t.action === "BUY" || t.action === "SHORT";
                    const isClose = t.action === "SELL" || t.action === "COVER";
                    const actionColor = isBuy ? "text-bt-positive border-bt-positive/40" : isClose ? "text-bt-gold border-bt-gold/40" : "text-bt-text-secondary border-bt-border-subtle";
                    return (
                      <div key={idx} className="grid min-w-[620px] grid-cols-[85px_60px_60px_70px_80px_80px_1fr] gap-2 px-3 py-2.5 border-b border-bt-border-subtle last:border-b-0 items-center text-xs font-mono hover:bg-bt-surface-2/50 transition-colors" data-testid={`trade-row-${idx}`}>
                        <div className="text-bt-text-tertiary text-[10px]">{when}</div>
                        <div><span className={`text-[10px] px-1.5 py-0.5 border ${actionColor}`}>{t.action}</span></div>
                        <div className="text-bt-text-primary font-semibold">{t.symbol}</div>
                        <div className="text-right text-bt-text-secondary">{Number(t.shares).toLocaleString(undefined, { maximumFractionDigits: 4 })}</div>
                        <div className="text-right text-bt-text-secondary">${Number(t.price).toFixed(2)}</div>
                        <div className="text-right">
                          {t.pnl !== undefined && t.pnl !== null ? (
                            <span className={`${t.pnl >= 0 ? "text-bt-positive" : "text-bt-negative"}`}>
                              {t.pnl >= 0 ? "+" : ""}{formatMoney(t.pnl)}
                            </span>
                          ) : (
                            <span className="text-bt-text-tertiary">—</span>
                          )}
                        </div>
                        <div className="text-[10px] text-bt-text-tertiary leading-tight pr-2">{t.reason}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
